import React, {
  FunctionComponent,
  useState,
  MouseEvent,
  useEffect,
} from "react"
import { useOrganizationRecovery } from "../hooks/useOrganizationRecovery"

import { DataColumn, Table } from "../components/table"
import {
  useAssignUsersToGroupMutation,
  useCsvExportBySearchMutation,
  useCsvExportMutation,
  useGetAllUsersByorganizationIdQuery,
  useGetGroupsByOrganizationIdQuery,
  useGetUsersByGroupIdsQuery,
  useRemoveUsersFromGroupMutation,
} from "../app/services/meseeknaApi"
import {
  useCreateOrganizationUserMutation,
  useGetOrganizationResultsAccessStatusQuery,
  useBulkReminderV2Mutation,
} from "../app/services/meseeknaApiV2"
import { RootState, useTypedSelector } from "../app/store"
import NotificationContainer from "../features/auth/components/Notifications/NotificationContainer"
import { saveAs } from "file-saver"
import Papa from "papaparse"
import ManageGroups from "./manage-groups"
import ImportUserForm from "./import-user-form"
import GroupsList from "./groups-list"
import { useLocation } from "react-router-dom"

// Tab type for switching between Users and Groups
type TabType = "users" | "groups"

type User = {
  id?: number
  email?: string
  firstName?: string
  lastName?: string
  role?: number
  password?: string
  expire_status?: number
  dateCompleted?: string | null
  roleName?: string
  savepoint?: string
  groupName?: string | null
}

type ExportUser = {
  firstName: string
  lastName: string
  email: string
  role: string
  groupName: string
  status: string | null
  activateLink: string
}

const RecruiterUserList: FunctionComponent = () => {
  const authStore = useTypedSelector((state: RootState) => {
    return state.authSlice
  })
  const location = useLocation()

  // Attempt to recover organization ID if it's missing
  const { isRecovering, recoverySuccessful } = useOrganizationRecovery()
  const columns = [
    { name: "firstName", label: "First Name", styles: "w-[12%]" },
    { name: "lastName", label: "Last Name", styles: "w-[12%]" },
    { name: "email", label: "Email", styles: "w-[25%]" },
    { name: "groupName", label: "Group Name", styles: "w-[15%]" },
    { name: "roleName", label: "Role Name", styles: "w-[12%]" },
    { name: "activated", label: "Activated", styles: "w-[12%]" },
    { name: "simulation", label: "Simulation", styles: "w-[12%]" },
  ]

  const [usersdata, setUsersData] = useState<DataColumn[][]>([])
  const [usersDataWithRowCheck, setUsersDataWithRowCheck] = useState<
    { check: boolean; index: number; usersDetails: DataColumn[] }[]
  >([])
  const [filterConfig, setFilterConfig] = useState({})
  const [selectedUsers, setSelectedUsers] = useState<
    { check: boolean; index: number; usersDetails: DataColumn[] }[]
  >([])
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [successMessage, setSuccessMessage] = useState<string>("")
  const [filteredUsers, setFilteredUsers] = useState<
    { check: boolean; index: number; usersDetails: DataColumn[] }[]
  >([])
  const [searchText, setSearchText] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // User creation state
  const [showCreateUserForm, setShowCreateUserForm] = useState(false)
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: 4
  })
  const [userCreationErrors, setUserCreationErrors] = useState({
    firstName: "",
    lastName: "",
    email: ""
  })
  const [touchedFields, setTouchedFields] = useState({
    firstName: false,
    lastName: false,
    email: false
  })
  const [isCreatingUser, setIsCreatingUser] = useState(false)
  const [filteredGroups, setFilteredGroups] = useState([])
  const [tableMetaData, setTableMetaData] = useState<
    { label: string; value: string | number }[]
  >([
    { label: "Total Users", value: 0 },
    { label: "Activated Users", value: 0 },
    { label: "Inactive Users", value: 0 },
    { label: "Simulation In Progress", value: 0 },
    { label: "Deactivated Users", value: 0 },
    { label: "Simulation Completed", value: 0 },
  ])
  const [openManageGroups, setOpenManageGroups] = useState<boolean>(false)
  const [groupsList, setGroupsList] = useState<
    { value: string; label: string }[]
  >([])
  const [displayList, setDisplayList] = useState<
    { value: string; label: string }[]
  >([])
  const [isAssignGroupsSubmitting, setIsAssignGroupsSubmitting] =
    useState<boolean>(false)
  const [showImportUsersForm, setShowImportUsersForm] = useState<boolean>(false)
  const [clearSearch, setClearSearch] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<TabType>("users")

  const [csvExport] = useCsvExportMutation()
  const [csvExportBySearch] = useCsvExportBySearchMutation()
  const [bulkReminderV2] = useBulkReminderV2Mutation()
  const [assignUsersToGroup] = useAssignUsersToGroupMutation()
  const [removeUsersFromGroup] = useRemoveUsersFromGroupMutation()
  const [createUser] = useCreateOrganizationUserMutation()

  // Role choices for user creation
  const ROLES_CHOICES = [
    {
      id: 4,
      code: 400,
      label: "Team Member",
      priceLabel: "first 10 included, $20/month additional",
    },
    {
      id: 3,
      code: 300,
      label: "Recruiter",
      priceLabel: "unlimited users, $80/month",
    },
  ]
  // Try to use organization ID from auth store, fall back to user's organizationId if available
  const effectiveOrgId = Number(
    authStore.organization?.id || authStore.user?.organizationId || 0,
  )


  const isAllUsers = location.pathname.split("/")[4] === "all"

  const allUsersQuery = useGetAllUsersByorganizationIdQuery({
    body: {
      organizationId: Number(authStore.organization?.id),
    },
  }, {
    skip: !isAllUsers // Skip this query if not viewing all users
  })

  const groupUsersQuery = useGetUsersByGroupIdsQuery({
    body: {
      organizationId: Number(authStore.organization.id),
      groupIds: [location.pathname.split("/")[4]],
    },
  }, {
    skip: isAllUsers // Skip this query if viewing all users
  })

  // Select the appropriate query result
  const {
    data: getAllUsers,
    isFetching,
    refetch: refetchUsers,
  } = isAllUsers ? allUsersQuery : groupUsersQuery

  const { data: groupsListResponse } = useGetGroupsByOrganizationIdQuery(
    {
      organizationId: effectiveOrgId,
    },
    {
      // Only skip if we're still recovering the organization data
      skip: isRecovering,
    },
  )

  // Check results access status for recruiter admin
  const { data: accessStatusData, isLoading: isLoadingAccessStatus } =
    useGetOrganizationResultsAccessStatusQuery(effectiveOrgId, {
      skip: !effectiveOrgId || isRecovering,
    })

  const accessStatus = accessStatusData?.data
  const hasResultsAccess = accessStatus?.hasAccess ?? true // Default to true to avoid blocking if API fails

  const rowClickAction = (e: MouseEvent<HTMLTableRowElement>) => {
    const rowId = e.currentTarget.id
    console.log("Clicked row with ID:", rowId)
  }

  const handleSelectionChange = (
    selectedRows: {
      check: boolean
      index: number
      usersDetails: DataColumn[]
    }[],
  ) => {
    setSelectedUsers(selectedRows)
  }

  const handleVisibleRowsChange = (
    visibleRows: {
      check: boolean
      index: number
      usersDetails: DataColumn[]
    }[],
  ) => {
    setFilteredUsers(visibleRows)
  }

  const handleManageGroups = () => {
    setOpenManageGroups(true)
  }

  const handleImportUsers = () => {
    setShowImportUsersForm(true)
  }

  const handleImportUsersCancel = () => {
    setShowImportUsersForm(false)
    refetchUsers()
    setClearSearch(true)
  }

  const handleExportUsers = async () => {
    setIsLoading(true)
    setFilteredGroups([])
    let payload
    const orgName = authStore.organization.organizationName

    if (searchText == "") {
      if (location.pathname.split("/")[4] !== "all") {
        payload = { groupId: location.state.group.id }
      } else if (filteredGroups.length > 0) {
        payload = { groupId: filteredGroups }
      } else {
        payload = { groupId: "" }
      }
    } else {
      const formatedUsersData: {
        id?: number
        email?: string
        firstName?: string
        lastName?: string
        role?: number
        password?: string
        expire_status?: number
        dateCompleted?: string | null
        roleName?: string
        savepoint?: string
        groupName?: string | null
        checked?: boolean
      }[] = []
      filteredUsers.forEach(
        (filteredUser: {
          check: boolean
          index: number
          usersDetails: DataColumn[]
        }) => {
          const emailObj = filteredUser.usersDetails.find(
            (item: DataColumn) => item.name === "email",
          )
          if (getAllUsers && "data" in getAllUsers && getAllUsers.data) {
            const formattedUsersList = getAllUsers.data.map((user: User) => {
              const userData = { ...user }
              if (userData.password == "Y") {
                userData.password = "Yes"
              } else {
                userData.password = "No"
              }
              if (userData.expire_status == 0) {
                userData.password = "Disabled"
              }
              if (userData.dateCompleted !== null) {
                userData.dateCompleted = "Completed"
              } else if (
                userData.dateCompleted === null &&
                userData.savepoint == "Y"
              ) {
                userData.dateCompleted = "In Progress"
              } else {
                userData.dateCompleted = "No"
              }
              return userData
            })
            const userData = formattedUsersList.find(
              (user: User) => user.email === emailObj?.value,
            )
            if (userData)
              formatedUsersData.push({ ...userData, checked: false })
          }
        },
      )

      payload = { data: formatedUsersData }
    }

    setSuccessMessage("")
    setErrorMessage("")

    try {
      const response =
        searchText == ""
          ? await csvExport({
              organizationId: effectiveOrgId,
              userId: String(authStore.user.id),
              body: payload,
            })
          : await csvExportBySearch({
              organizationId: effectiveOrgId,
              userId: String(authStore.user.id),
              body: payload,
            })

      if ("data" in response && "data" in response.data && response.data.data) {
        const users = response.data.data
        const userRows = users.slice(1)
        const fields: (keyof ExportUser)[] = Object.keys(
          users[0],
        ) as (keyof ExportUser)[]

        const csvData = userRows.map((user) =>
          fields.map((field) => user[field] || ""),
        )

        const csvHeaders = fields.map((key) => {
          return key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
        })

        const csv = Papa.unparse({
          fields: csvHeaders,
          data: csvData,
        })

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })

        saveAs(
          blob,
          `${(orgName ? orgName : "").replace(/\s+/g, "_") + "_users"}.csv`,
        )
      }
    } catch (error) {
      console.error("Error bulk reminder:", error)
    }
    setIsLoading(false)
  }

  const bulkExpire = async (status: string) => {
    // Extract userIds from selected users
    const userIds: number[] = []
    selectedUsers.forEach(
      (user: { check: boolean; usersDetails: DataColumn[] }) => {
        const userIdObj = user.usersDetails.find(
          (item: DataColumn) => item.name === "userId",
        )
        if (userIdObj && userIdObj.value) {
          userIds.push(Number(userIdObj.value))
        }
      },
    )

    if (userIds.length === 0) {
      setErrorMessage("No valid users selected")
      return
    }

    // Warn but don't block if the organization ID seems suspect
    if (!authStore.organization?.id && !authStore.user?.organizationId) {
      console.warn("Using default organization ID for bulk operation")
    }
    setSuccessMessage("")
    setErrorMessage("")
    setIsLoading(true)
    try {
      const response = await bulkReminderV2({
        userIds,
        status,
      }).unwrap()
      if (response.status === "success") {
        setSelectedUsers([])
        refetchUsers()
        setClearSearch(true)
        if (status === "reminder") {
          const sentCount = response.data?.sent || userIds.length
          setSuccessMessage(`Email reminder${sentCount > 1 ? 's' : ''} sent successfully (${sentCount} user${sentCount > 1 ? 's' : ''})`)
        } else {
          setSuccessMessage("User deactivated successfully")
        }
      }
    } catch (error) {
      console.error("Error bulk reminder:", error)
      const errorMsg = error && typeof error === 'object' && 'data' in error
        ? (error as { data?: { message?: string } }).data?.message || "Failed to send reminder emails"
        : "Failed to send reminder emails"
      setErrorMessage(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmitManageGroups = async (
    groupsToAdd: { value: string; label: string }[],
    groupToRemove: string,
  ) => {
    setIsAssignGroupsSubmitting(true)
    let formStatus = ""
    let toasterMsg = ""
    const groupIdsToAdd = groupsToAdd.map(
      (group: { value: string; label: string }) => {
        return Number(group.value)
      },
    )
    if (groupsToAdd.length > 0 && !groupToRemove) {
      formStatus = "Assign"
      toasterMsg = "Users grouped successfully"
    } else if (groupToRemove && groupsToAdd.length === 0) {
      formStatus = "Remove"
      toasterMsg = "Users removed from group successfully"
    } else if (!groupToRemove && groupsToAdd.length === 0) {
      formStatus = "Empty"
      setErrorMessage("Please select group")
      return
    } else {
      formStatus = "both"
      setErrorMessage("Please select only one operation at once")
      return
    }

    if (formStatus == "Assign" || formStatus == "Remove") {
      const assignedUsers: number[] = []
      selectedUsers.forEach(
        (selectedUser: { check: boolean; usersDetails: DataColumn[] }) => {
          const emailObj = selectedUser.usersDetails.find(
            (item: DataColumn) => item.name === "email",
          )
          if (
            emailObj &&
            getAllUsers &&
            "data" in getAllUsers &&
            getAllUsers.data
          ) {
            const userObj = getAllUsers.data.find(
              (user: User) => user.email === emailObj.value,
            )
            if (userObj && userObj.id) {
              assignedUsers.push(userObj.id)
            }
          }
        },
      )
      try {
        let response
        if (formStatus == "Assign") {
          const assignUsersToGroupPayload = {
            userIds: assignedUsers,
            groupIds: groupIdsToAdd,
            organizationId: effectiveOrgId,
            createdBy: authStore.user.id,
          }

          response = await assignUsersToGroup({
            body: assignUsersToGroupPayload,
          }).unwrap()
        } else {
          const removeUsersFromGroupPayload = {
            userIds: assignedUsers,
            groupId: Number(groupToRemove),
            organizationId: effectiveOrgId,
            createdBy: authStore.user.id,
          }

          response = await removeUsersFromGroup({
            body: removeUsersFromGroupPayload,
          }).unwrap()
        }

        if (response.status == "success") {
          if (formStatus == "Remove") {
            if (
              "data" in response &&
              response.data &&
              response.data.affectedRows === 1
            ) {
              setSuccessMessage(toasterMsg)
            } else {
              setSuccessMessage("Selected users are not assigned to this group")
            }
          } else {
            if (
              "existingUsersData" in response &&
              Array.isArray(response.existingUsersData) &&
              response.existingUsersData.length > 0
            ) {
              setSuccessMessage(
                "Some users already exist in recpective group and other users are grouped successfully",
              )
            } else {
              setSuccessMessage(toasterMsg)
            }
          }
        }
      } catch (error) {
        console.error("Error bulk reminder:", error)
      }
    }
    setIsAssignGroupsSubmitting(false)
    handleCloseManageGroups()
  }

  const handleCloseManageGroups = () => {
    setOpenManageGroups(false)
    setSelectedUsers([])
    refetchUsers()
    setClearSearch(true)
  }

  const getDefaultGroup = () => {
    return location.pathname.split("/")[4] !== "all"
      ? groupsList.filter(
          (group: { value: string; label: string }) =>
            group.value === String(location.state.group.id),
        )
      : []
  }

  // User creation helper functions
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const isValidName = (name: string): boolean => {
    return /^[a-zA-Z\s'.-]+$/.test(name)
  }

  const validateUserField = (field: "firstName" | "lastName" | "email", value: string): string => {
    switch (field) {
      case "firstName":
        if (!value.trim()) return "First name is required"
        if (!isValidName(value)) return "Please enter a valid first name"
        if (value.trim().length < 2) return "First name must be at least 2 characters"
        return ""
      case "lastName":
        if (!value.trim()) return "Last name is required"
        if (!isValidName(value)) return "Please enter a valid last name"
        if (value.trim().length < 2) return "Last name must be at least 2 characters"
        return ""
      case "email":
        if (!value.trim()) return "Email is required"
        if (!isValidEmail(value.trim())) return "Please enter a valid email address"
        return ""
      default:
        return ""
    }
  }

  const resetUserForm = () => {
    setNewUser({
      firstName: "",
      lastName: "",
      email: "",
      role: 4
    })
    setUserCreationErrors({
      firstName: "",
      lastName: "",
      email: ""
    })
    setTouchedFields({
      firstName: false,
      lastName: false,
      email: false
    })
  }

  const handleUserFieldChange = (field: string, value: string | number) => {
    setNewUser({
      ...newUser,
      [field]: value,
    })

    if (field !== "role") {
      const errorMessage = validateUserField(field as "firstName" | "lastName" | "email", value as string)
      setUserCreationErrors({
        ...userCreationErrors,
        [field]: errorMessage,
      })
    }
  }

  const handleUserFieldBlur = (field: "firstName" | "lastName" | "email") => {
    setTouchedFields({
      ...touchedFields,
      [field]: true,
    })

    const errorMessage = validateUserField(field, newUser[field] as string)
    setUserCreationErrors({
      ...userCreationErrors,
      [field]: errorMessage,
    })
  }

  const canSubmitUser = newUser.firstName.trim() && newUser.lastName.trim() && isValidEmail(newUser.email.trim()) && !Object.values(userCreationErrors).some(error => error)

  const handleCreateUser = (roleId: number) => {
    setTouchedFields({
      firstName: true,
      lastName: true,
      email: true,
    })

    const firstNameError = validateUserField("firstName", newUser.firstName)
    const lastNameError = validateUserField("lastName", newUser.lastName)
    const emailError = validateUserField("email", newUser.email)

    setUserCreationErrors({
      firstName: firstNameError,
      lastName: lastNameError,
      email: emailError,
    })

    if (!canSubmitUser || firstNameError || lastNameError || emailError) return

    setIsCreatingUser(true)

    createUser({
      firstName: newUser.firstName.trim(),
      lastName: newUser.lastName.trim(),
      email: newUser.email.trim(),
      role: roleId,
    })
      .unwrap()
      .then((createResult) => {
        console.log("User creation successful:", createResult)
        resetUserForm()
        setSuccessMessage("User created successfully")
        setShowCreateUserForm(false)
        refetchUsers()
      })
      .catch((error: unknown) => {
        console.error("User creation error:", error)
        // RTK Query errors can have different structures
        const err = error as {
          data?: { message?: string; status?: string }
          error?: string
          message?: string
          status?: number
        }
        // Try multiple paths to extract the error message
        const errorMessage =
          err.data?.message ||
          err.error ||
          err.message ||
          (typeof error === 'string' ? error : "Failed to create user")
        setErrorMessage(errorMessage)
      })
      .finally(() => {
        setIsCreatingUser(false)
      })
  }

  const buttons = [
    {
      action: () => setShowCreateUserForm(true),
      label: "Create User",
      disable: false,
    },
    {
      action: handleManageGroups,
      label: "Manage Groups",
      disable: selectedUsers.length === 0,
    },
    {
      action: () => bulkExpire("reminder"),
      label: isLoading ? "Sending..." : "E-mail Reminder",
      disable: selectedUsers.length === 0 || isLoading,
    },
    {
      action: () => bulkExpire("expire"),
      label: "Deactivate User",
      disable: selectedUsers.length === 0 || isLoading,
    },
    {
      action: handleImportUsers,
      label: "Import Users",
      disable: false,
    },
    {
      action: handleExportUsers,
      label: "Export Users",
      disable: false,
    },
  ]

  useEffect(() => {
    if (getAllUsers && "data" in getAllUsers && getAllUsers.data) {
      const totalUsersActive = []
      const totalUsersInActive = []
      const userSimInprogress = []
      const expiredUsers = []
      const userSimCompleted = []
      const totalUsers = []
      const formattedUsersList = getAllUsers.data.map((user: User) => {
        const userData = { ...user }
        if (userData.password == "Y" && userData.expire_status != 0) {
          totalUsersActive.push(userData)
        }
        if (userData.password == "N" && userData.expire_status != 0) {
          totalUsersInActive.push(userData)
        }
        userData.expire_status == 0
          ? expiredUsers.push(userData)
          : totalUsers.push(userData)
        if (userData.savepoint == "Y") {
          userSimInprogress.push(userData)
        }
        if (userData.dateCompleted !== null && userData.expire_status != 0) {
          userSimCompleted.push(userData)
        }
        if (userData.password == "Y") {
          userData.password = "Yes"
        } else {
          userData.password = "No"
        }
        if (userData.expire_status == 0) {
          userData.password = "Disabled"
        }
        if (userData.dateCompleted !== null) {
          userData.dateCompleted = "Completed"
        } else if (
          userData.dateCompleted === null &&
          userData.savepoint == "Y"
        ) {
          userData.dateCompleted = "In Progress"
        } else {
          userData.dateCompleted = "No"
        }
        return userData
      })
      const groupNameValues: string[] = []
      const activatedValues: string[] = []
      const simulationValues: string[] = []
      const usersDataWithRowCheckInsert: {
        check: boolean
        index: number
        usersDetails: DataColumn[]
      }[] = []
      const usersDataList = formattedUsersList.map(
        (user: User, index: number) => {
          if (user.groupName) groupNameValues.push(user.groupName)
          if (user.password) activatedValues.push(user.password)
          if (user.dateCompleted) simulationValues.push(user.dateCompleted)
          const returnValue = [
            {
              id: (index * 8 + 1).toString(),
              name: "userId",
              value: user.id ? user.id.toString() : "",
            },
            {
              id: (index * 8 + 2).toString(),
              name: "firstName",
              value: user.firstName ? user.firstName : "",
            },
            {
              id: (index * 8 + 3).toString(),
              name: "lastName",
              value: user.lastName ? user.lastName : "",
            },
            {
              id: (index * 8 + 4).toString(),
              name: "email",
              value: user.email ? user.email : "",
            },
            {
              id: (index * 8 + 5).toString(),
              name: "groupName",
              value: user.groupName ? user.groupName : "",
            },
            {
              id: (index * 8 + 6).toString(),
              name: "roleName",
              value: user.roleName ? user.roleName : "",
            },
            {
              id: (index * 8 + 7).toString(),
              name: "activated",
              value: user.password ? user.password : "",
            },
            {
              id: (index * 8 + 8).toString(),
              name: "simulation",
              value: user.dateCompleted ? user.dateCompleted : "",
            },
          ]

          usersDataWithRowCheckInsert.push({
            check: false,
            index: index,
            usersDetails: returnValue,
          })
          return returnValue
        },
      )

      // Get all group names from groupsListResponse for the filter (not just assigned groups)
      const allGroupNames = groupsListResponse?.data?.map((group) => group.name) || []

      const filterConfigList = {
        groupName: { values: allGroupNames.length > 0 ? allGroupNames : Array.from(new Set(groupNameValues)) },
        activated: { values: Array.from(new Set(activatedValues)) },
        simulation: { values: Array.from(new Set(simulationValues)) },
      }

      const metadata = [
        { label: "Total Users", value: totalUsers.length },
        { label: "Activated Users", value: totalUsersActive.length },
        { label: "Inactive Users", value: totalUsersInActive.length },
        { label: "Simulation In Progress", value: userSimInprogress.length },
        { label: "Deactivated Users", value: expiredUsers.length },
        { label: "Simulation Completed", value: userSimCompleted.length },
      ]
      setUsersDataWithRowCheck(usersDataWithRowCheckInsert)
      setUsersData(usersDataList)
      setFilterConfig(filterConfigList)
      setTableMetaData(metadata)
    }
  }, [getAllUsers, groupsListResponse])

  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  useEffect(() => {
    if (
      groupsListResponse &&
      groupsListResponse.status === "success" &&
      "data" in groupsListResponse &&
      groupsListResponse.data &&
      groupsListResponse.data.length > 0
    ) {
      const groupsArray = groupsListResponse.data.map((group) => {
        return {
          value: String(group.id),
          label: group.name,
        }
      })
      setGroupsList(groupsArray)
      console.log("location", location)
      setDisplayList(
        location.pathname.split("/")[4] !== "all"
          ? groupsArray.filter(
              (group: { value: string; label: string }) =>
                group.value === String(location.state?.group?.id),
            )
          : groupsArray,
      )
    }
  }, [groupsListResponse])

  // Refetch data when organization recovery is successful
  useEffect(() => {
    if (recoverySuccessful && authStore.organization?.id) {
      console.log("Organization recovery successful, refetching data")
      refetchUsers()
    }
  }, [recoverySuccessful, authStore.organization?.id])

  // Display loading message while recovering organization data
  if (isRecovering) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite] mb-4"></div>
          <p>Loading user data...</p>
        </div>
      </div>
    )
  }

  // If we failed to recover organization ID but we're not still recovering,
  // proceed but with warnings in the console
  if (
    !isRecovering &&
    (!authStore.organization?.id || isNaN(Number(authStore.organization.id)))
  ) {
    console.warn(
      "Organization ID is missing or invalid, but proceeding anyway",
      {
        organizationId: authStore.organization?.id,
        userId: authStore.user?.id,
        userOrgId: authStore.user?.organizationId,
      },
    )
  }

  return (
    <div>
      <NotificationContainer
        errorMessage={errorMessage}
        successMessage={successMessage}
      />
      {/* Tabs Navigation - only show when not in forms */}
      {!showImportUsersForm && !showCreateUserForm && (
        <div className="px-8 pt-2">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab("users")}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "users"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                  </svg>
                  Users
                </div>
              </button>
              <button
                onClick={() => setActiveTab("groups")}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "groups"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                  </svg>
                  Groups
                </div>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Groups Tab Content */}
      {activeTab === "groups" && !showImportUsersForm && !showCreateUserForm ? (
        <GroupsList />
      ) : showImportUsersForm ? (
        <ImportUserForm
          groupsList={groupsList}
          handleCancel={handleImportUsersCancel}
          disableGroup={location.pathname.split("/")[4] !== "all"}
          defaultGroup={
            location.pathname.split("/")[4] !== "all"
              ? String(location.state?.group?.id || location.pathname.split("/")[4])
              : ""
          }
        />
      ) : showCreateUserForm ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Create New User</h2>
              <button
                onClick={() => {
                  setShowCreateUserForm(false)
                  resetUserForm()
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <form className="space-y-4">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={newUser.firstName}
                  onChange={(e) => handleUserFieldChange("firstName", e.target.value)}
                  onBlur={() => handleUserFieldBlur("firstName")}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    touchedFields.firstName && userCreationErrors.firstName
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter first name"
                />
                {touchedFields.firstName && userCreationErrors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{userCreationErrors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={newUser.lastName}
                  onChange={(e) => handleUserFieldChange("lastName", e.target.value)}
                  onBlur={() => handleUserFieldBlur("lastName")}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    touchedFields.lastName && userCreationErrors.lastName
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter last name"
                />
                {touchedFields.lastName && userCreationErrors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{userCreationErrors.lastName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => handleUserFieldChange("email", e.target.value)}
                  onBlur={() => handleUserFieldBlur("email")}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    touchedFields.email && userCreationErrors.email
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter email address"
                />
                {touchedFields.email && userCreationErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{userCreationErrors.email}</p>
                )}
              </div>

              {/* Role Selection Buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Role
                </label>
                <div className="space-y-2">
                  {ROLES_CHOICES.map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      disabled={!canSubmitUser || isCreatingUser}
                      onClick={() => handleCreateUser(role.id)}
                      className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <div className="font-medium text-gray-900">{role.label}</div>
                      <div className="text-sm text-gray-500">{role.priceLabel}</div>
                    </button>
                  ))}
                </div>
              </div>
            </form>

            {isCreatingUser && (
              <div className="mt-4 text-center">
                <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-blue-600 border-r-transparent"></div>
                <span className="ml-2 text-gray-600">Creating user...</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <React.Fragment>
          {!isLoadingAccessStatus && !hasResultsAccess && (
            <div className="mx-8 mt-2 mb-0 bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#6182F7]/10 flex items-center justify-center">
                  <svg
                    className="h-5 w-5 text-[#6182F7]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900">
                    Results Pending Approval
                  </h3>
                  <p className="text-sm text-gray-500">
                    {accessStatus?.message ||
                      "Results are pending super admin approval. You'll be notified via email when results are available."}
                  </p>
                  {getAllUsers?.data && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="h-full bg-[#6182F7] rounded-full transition-all"
                          style={{
                            width: `${Math.round((getAllUsers.data.filter((u: User) => u.dateCompleted !== null).length / getAllUsers.data.length) * 100)}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs text-gray-600 whitespace-nowrap">
                        {getAllUsers.data.filter((u: User) => u.dateCompleted !== null).length} of {getAllUsers.data.length} completed
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <Table
            columns={columns}
            data={usersdata}
            buttons={buttons}
            rowClickAction={rowClickAction}
            onSelectionChange={handleSelectionChange}
            showCheckboxColumn={true}
            onVisibleRowsChange={handleVisibleRowsChange}
            filterConfig={filterConfig}
            loading={isLoading || isFetching}
            setSearchText={setSearchText}
            clearSearch={clearSearch}
            setClearSearch={setClearSearch}
            metaData={tableMetaData}
            newData={usersDataWithRowCheck}
            addPadding={false}
          />
        </React.Fragment>
      )}

      <ManageGroups
        isOpen={openManageGroups}
        handleCancel={handleCloseManageGroups}
        handleSubmit={handleSubmitManageGroups}
        groupsList={groupsList}
        displayList={displayList}
        isSubmitting={isAssignGroupsSubmitting}
        fromGroupsPage={location.pathname.split("/")[4] !== "all"}
        defaultGroup={getDefaultGroup()}
      />
    </div>
  )
}

export default RecruiterUserList
